/**
 * OVERLAY MANAGER
 *
 * Centralized controller for overlay state coordination across Shopify theme
 * and Nosto components. Manages ScrollLock, body classes, and z-index layer
 * transitions through a single, semantic API.
 *
 * @module OverlayManager
 * @requires ScrollLock (window.ScrollLock from scroll-lock-utility.js)
 */

(function() {
  'use strict';

  // =========================================================================
  // CONSTANTS
  // =========================================================================

  var SCROLL_LOCK_STRATEGY = {
    FIXED: 'fixed',
    OVERFLOW: 'overflow'
  };

  var OVERLAY_CONFIG = {
    search: {
      scrollLock: SCROLL_LOCK_STRATEGY.FIXED,
      bodyClasses: ['has-search-open', 'has-overlay-open'],
      removeOnConflict: ['has-drawer-open', 'has-quick-add-open', 'has-sort-drawer-open']
    },
    cart: {
      scrollLock: SCROLL_LOCK_STRATEGY.FIXED,
      bodyClasses: ['has-drawer-open', 'has-overlay-open'],
      removeOnConflict: ['has-search-open', 'has-quick-add-open', 'has-sort-drawer-open']
    },
    drawer: {
      scrollLock: SCROLL_LOCK_STRATEGY.FIXED,
      bodyClasses: ['has-drawer-open', 'has-overlay-open'],
      removeOnConflict: ['has-search-open', 'has-quick-add-open', 'has-sort-drawer-open']
    },
    filter: {
      scrollLock: SCROLL_LOCK_STRATEGY.FIXED,
      bodyClasses: ['has-drawer-open', 'has-overlay-open'],
      removeOnConflict: ['has-search-open', 'has-quick-add-open', 'has-sort-drawer-open']
    },
    sort: {
      scrollLock: SCROLL_LOCK_STRATEGY.OVERFLOW,
      bodyClasses: ['has-sort-drawer-open'],
      removeOnConflict: ['has-search-open', 'has-drawer-open', 'has-quick-add-open']
    },
    quickAdd: {
      // Uses FIXED strategy for reliable iOS Safari scroll locking.
      // Trade-off: iOS status bar will be white (browser limitation when body has position:fixed).
      // This is acceptable - stability > cosmetics.
      scrollLock: SCROLL_LOCK_STRATEGY.FIXED,
      bodyClasses: ['has-quick-add-open', 'has-overlay-open'],
      removeOnConflict: ['has-search-open', 'has-drawer-open', 'has-sort-drawer-open']
    }
  };

  // =========================================================================
  // STATE
  // =========================================================================

  var activeOverlays = [];
  var pendingUnlocks = {};

  // =========================================================================
  // PRIVATE METHODS
  // =========================================================================

  function applyScrollLock(strategy, forceApply) {
    if (strategy === SCROLL_LOCK_STRATEGY.FIXED) {
      if (window.ScrollLock && typeof window.ScrollLock.lock === 'function') {
        window.ScrollLock.lock(forceApply);
      } else {
        document.documentElement.classList.add('no-scroll');
      }
    } else if (strategy === SCROLL_LOCK_STRATEGY.OVERFLOW) {
      document.documentElement.classList.add('no-scroll');
    }
  }

  function removeScrollLock(strategy) {
    if (strategy === SCROLL_LOCK_STRATEGY.FIXED) {
      if (window.ScrollLock && typeof window.ScrollLock.unlock === 'function') {
        window.ScrollLock.unlock();
      } else {
        document.documentElement.classList.remove('no-scroll');
      }
    } else if (strategy === SCROLL_LOCK_STRATEGY.OVERFLOW) {
      document.documentElement.classList.remove('no-scroll');
    }
  }

  function addBodyClasses(classes) {
    classes.forEach(function(cls) {
      document.body.classList.add(cls);
    });
  }

  function removeBodyClasses(classes) {
    classes.forEach(function(cls) {
      document.body.classList.remove(cls);
    });
  }

  function handleConflicts(config) {
    if (!config.removeOnConflict || config.removeOnConflict.length === 0) {
      return false;
    }

    var conflictingOverlays = [];
    var hasFixedConflict = false;

    activeOverlays.forEach(function(activeType) {
      var activeConfig = OVERLAY_CONFIG[activeType];
      if (activeConfig) {
        var hasConflict = activeConfig.bodyClasses.some(function(cls) {
          return config.removeOnConflict.indexOf(cls) > -1;
        });
        if (hasConflict) {
          conflictingOverlays.push({ type: activeType, config: activeConfig });
          if (activeConfig.scrollLock === SCROLL_LOCK_STRATEGY.FIXED) {
            hasFixedConflict = true;
          }
        }
      }
    });

    // Reset scroll lock state when transitioning between FIXED overlays
    if (hasFixedConflict && window.ScrollLock && typeof window.ScrollLock.forceReset === 'function') {
      window.ScrollLock.forceReset();
    }

    conflictingOverlays.forEach(function(item) {
      if (item.config.scrollLock === SCROLL_LOCK_STRATEGY.OVERFLOW) {
        document.documentElement.classList.remove('no-scroll');
      }
      var index = activeOverlays.indexOf(item.type);
      if (index > -1) {
        activeOverlays.splice(index, 1);
      }
    });

    removeBodyClasses(config.removeOnConflict);

    if (conflictingOverlays.length > 0) {
      document.body.classList.remove('has-overlay-open');
    }

    return hasFixedConflict;
  }

  function getConfig(type) {
    return OVERLAY_CONFIG[type] || null;
  }

  // =========================================================================
  // PUBLIC API
  // =========================================================================

  window.OverlayManager = {

    STRATEGY: SCROLL_LOCK_STRATEGY,

    open: function(type) {
      var config = getConfig(type);
      if (!config) {
        return false;
      }

      var hadFixedConflict = handleConflicts(config);
      addBodyClasses(config.bodyClasses);

      var forceApply = hadFixedConflict && config.scrollLock === SCROLL_LOCK_STRATEGY.FIXED;
      applyScrollLock(config.scrollLock, forceApply);

      if (activeOverlays.indexOf(type) === -1) {
        activeOverlays.push(type);
      }

      return true;
    },

    close: function(type, options) {
      options = options || {};
      var config = getConfig(type);
      if (!config) {
        return false;
      }

      // Check if overlay was actually active BEFORE removing
      var index = activeOverlays.indexOf(type);
      var wasActive = index > -1;

      if (wasActive) {
        activeOverlays.splice(index, 1);
      }

      // Always remove body classes (safe even if they weren't there)
      removeBodyClasses(config.bodyClasses);

      // Only handle scroll lock if overlay was actually active.
      // This prevents the bug where close('search') stores a pendingUnlock
      // even though search was never opened, which later undoes the real overlay's lock.
      if (wasActive) {
        if (options.delayScrollUnlock) {
          pendingUnlocks[type] = config.scrollLock;
        } else {
          removeScrollLock(config.scrollLock);
        }
      }

      return wasActive;
    },

    completeClose: function(type) {
      var strategy = pendingUnlocks[type];
      if (strategy) {
        removeScrollLock(strategy);
        delete pendingUnlocks[type];
        return true;
      }
      return false;
    },

    getActive: function() {
      return activeOverlays.slice();
    },

    isActive: function(type) {
      return activeOverlays.indexOf(type) > -1;
    },

    hasActiveOverlay: function() {
      return activeOverlays.length > 0;
    },

    closeAll: function() {
      var self = this;
      while (activeOverlays.length > 0) {
        var type = activeOverlays[activeOverlays.length - 1];
        self.close(type);
      }
      pendingUnlocks = {};
    },

    register: function(type, config) {
      if (OVERLAY_CONFIG[type]) {
        return false;
      }
      if (!config.scrollLock || !config.bodyClasses) {
        return false;
      }
      OVERLAY_CONFIG[type] = {
        scrollLock: config.scrollLock,
        bodyClasses: config.bodyClasses,
        removeOnConflict: config.removeOnConflict || []
      };
      return true;
    }
  };

})();
